import React, { useEffect, useState } from "react";
import { MapPin, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const STORAGE_KEY = "companin_loc_prompt_v1";

/**
 * Soft, non-blocking banner that appears on a guest's first Discovery visit.
 * Asks for browser geolocation; if approved, hands the {lat,lng} back to the
 * parent via onLocationGranted(). User can dismiss; we remember the choice.
 */
export default function LocationPromptBanner({ onLocationGranted, hasCoords }) {
  const [dismissed, setDismissed] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (hasCoords) {
      setDismissed(true);
      return;
    }
    try {
      const v = localStorage.getItem(STORAGE_KEY);
      if (v === "allowed" || v === "skipped") setDismissed(true);
    } catch {
      /* SSR / private mode — show banner */
    }
  }, [hasCoords]);

  if (dismissed || hasCoords) return null;

  const allow = () => {
    if (!navigator.geolocation) {
      toast.error("Your browser doesn't support location");
      return;
    }
    setBusy(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setBusy(false);
        try {
          localStorage.setItem(STORAGE_KEY, "allowed");
        } catch {}
        setDismissed(true);
        onLocationGranted?.({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
        });
        toast.success("Showing companions near you");
      },
      (err) => {
        setBusy(false);
        try {
          localStorage.setItem(STORAGE_KEY, "skipped");
        } catch {}
        setDismissed(true);
        toast.error(err.code === 1 ? "Permission denied" : "Could not get location");
      },
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 600000 }
    );
  };

  const skip = () => {
    try {
      localStorage.setItem(STORAGE_KEY, "skipped");
    } catch {}
    setDismissed(true);
  };

  return (
    <div
      className="mb-5 rounded-2xl border border-emerald-200 bg-emerald-50 p-4 flex items-center gap-3 shadow-sm"
      data-testid="location-prompt-banner"
    >
      <div className="h-10 w-10 rounded-xl bg-white grid place-items-center text-emerald-700 shrink-0 shadow-sm">
        <MapPin className="h-5 w-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-stone-900 text-sm">Show companions near you?</div>
        <p className="text-xs text-stone-600 leading-relaxed">
          Use your device location to see distance and sort by closest first. Always optional.
        </p>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <Button
          size="sm"
          onClick={allow}
          disabled={busy}
          className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white h-9 px-4"
          data-testid="location-prompt-allow"
        >
          {busy ? "Detecting…" : "Allow"}
        </Button>
        <button
          onClick={skip}
          className="h-9 w-9 rounded-full text-stone-500 hover:text-stone-900 hover:bg-white grid place-items-center"
          aria-label="Dismiss"
          data-testid="location-prompt-skip"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
